import { RRRFetch } from '@cuemath/rest-api';
import { SYSTEM_EVENT_TYPE, TICKET_STATUS_CHANGE_EVENTS } from './constants';

// ── Email helpers ─────────────────────────────────────────────────────

export const isValidEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export const parseEmailList = (str, validate = true) =>
  (str || '')
    .split(',')
    .map(e => e.trim())
    .filter(e => e && (!validate || isValidEmail(e)));

// Fold a recipient input's uncommitted draft into its chip list. Returns the
// resulting list plus the offending value when the draft is a non-empty
// invalid email, so callers can flush the draft at send time (the chip
// input's own setState hasn't applied yet inside the send closure).
export const commitDraftToList = (draft, list) => {
  const trimmed = (draft || '')
    .trim()
    .replace(/,$/, '')
    .trim();
  if (!trimmed) return { list, invalid: null };
  if (!isValidEmail(trimmed)) return { list, invalid: trimmed };
  return { list: list.includes(trimmed) ? list : [...list, trimmed], invalid: null };
};

export const escapeHtml = text =>
  (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');

// ── File upload ───────────────────────────────────────────────────────

export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024,
  ALLOWED_TYPES: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

export const getMessageType = mimeType =>
  mimeType?.startsWith('image/') ? 'IMAGE' : 'FILE';

export const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const uploadAttachment = async file => {
  if (file.size > FILE_UPLOAD.MAX_SIZE) {
    throw new Error('File must be under 10 MB');
  }
  if (!FILE_UPLOAD.ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Unsupported file type');
  }
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await RRRFetch.fetch('/api/v1/chatbot/uploads/', {
    method: 'POST',
    body: formData,
  });
  // `media_url` is the canonical (unsigned) S3 URL to persist; `url` is a
  // short-lived presigned GET used only to preview the file optimistically —
  // the raw canonical URL 403s against the private bucket.
  const url = data?.media_url || data?.url;
  if (!url) throw new Error('Upload failed');
  return {
    url,
    displayUrl: data?.url || url,
    name: file.name,
    messageType: getMessageType(file.type),
  };
};

export const getFileNameFromUrl = url => {
  if (!url) return 'Attachment';
  try {
    const path = new URL(url).pathname;
    const parts = path.split('/');
    return decodeURIComponent(parts[parts.length - 1]) || 'Attachment';
  } catch {
    return 'Attachment';
  }
};

export const getDateLabel = rawTs => {
  if (!rawTs) return null;
  const date = new Date(rawTs);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Maps a backend message object to the UI format used by ThreadItem.
 * Handles both v1 payload (type/system_event_type/timestamp) and legacy format.
 * If the message already has a `kind` field (optimistic/mock), it passes through unchanged.
 */
export const mapBackendMessage = m => {
  if (m.kind) return m;

  const id = m.id || m.message_id;
  const senderMap = {
    CUSTOMER: 'user',
    customer: 'user',
    user: 'user',
    parent: 'user',
    teacher: 'user',
    AGENT: 'agent',
    agent: 'agent',
    BOT: 'bot',
    bot: 'bot',
    SYSTEM: 'system',
    system: 'system',
    EXTERNAL: 'external',
  };

  const isExternalMessage =
    Boolean(m.external_kind) || m.item_type === 'EXTERNAL_MESSAGE';
  const isNote = m.type === 'NOTE' || m.sender_type === 'note' || m.item_type === 'NOTE';
  const isSystemEvent =
    m.type === 'SYSTEM_EVENT' ||
    m.sender_type === 'system' ||
    m.item_type === 'SYSTEM_MESSAGE';
  // Ticket status transitions render as inline system pills (same UI as
  // CATEGORY_CHANGED), even when published on support_agent_internal.<id>
  // where item_type may be tagged SYSTEM_INTERNAL_MESSAGE.
  const isStatusChangeEvent = TICKET_STATUS_CHANGE_EVENTS.includes(m.system_event_type);
  const isInternalEvent =
    !isStatusChangeEvent &&
    (m.item_type === 'SYSTEM_INTERNAL_MESSAGE' ||
      m.system_event_type === SYSTEM_EVENT_TYPE.BOT_SUMMARY ||
      m.system_event_type === SYSTEM_EVENT_TYPE.FRT_BREACHED ||
      m.system_event_type === SYSTEM_EVENT_TYPE.SLA_WARNING);

  const rawTs = m.timestamp || m.created_at;
  const ts = rawTs
    ? new Date(rawTs).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '';

  if (isNote) {
    return {
      id,
      rawTs,
      kind: 'note',
      body: m.content || '',
      author: m.sender_name || '',
      ts,
      messageType: m.message_type || 'TEXT',
      mediaUrl: m.media_url || null,
      fileName: m.file_name || null,
      emailSubject: m.email_subject || null,
      emailCc: m.email_cc || null,
      emailBcc: m.email_bcc || null,
      emailToList: m.email_to_list || null,
      senderEmail: m.sender_email || null,
    };
  }
  if (isExternalMessage) {
    // sender_type here is AGENT (we sent it out) or EXTERNAL (the third party replied)
    const direction = m.sender_type === 'AGENT' ? 'sent' : 'received';
    return {
      id,
      rawTs,
      kind: 'external',
      who: senderMap[m.sender_type] || 'external',
      role: m.sender_type || '',
      direction,
      externalKind: m.external_kind || 'EMAIL',
      messageId: m.message_id || id,
      body: m.content || '',
      ts,
      senderName: m.sender_name || null,
      tempId: m.temp_id || null,
      // Own email fields — never fall back to ticket-level email props.
      htmlContent: m.html_content || null,
      emailSubject: m.email_subject || null,
      emailCc: m.email_cc || null,
      emailBcc: m.email_bcc || null,
      emailToList: m.email_to_list || null,
      senderEmail: m.sender_email || null,
      messageType: m.message_type || 'TEXT',
      mediaUrl: m.media_url || null,
      fileName: m.file_name || null,
    };
  }
  if (isSystemEvent && !isInternalEvent) {
    return {
      id,
      rawTs,
      kind: 'system',
      tint: 'info',
      body: m.content || '',
      title: (m.system_event_type || '').replace(/_/g, ' '),
    };
  }
  if (isInternalEvent) {
    return { id, rawTs, kind: 'event', icon: 'sparks', title: m.content || '', sub: ts };
  }
  const who = senderMap[m.sender_type] || '';
  return {
    id,
    rawTs,
    kind: 'msg',
    who,
    role: m.sender_type || '',
    name: m.sender_name || '',
    body: m.content || '',
    ts,
    messageType: m.message_type || 'TEXT',
    mediaUrl: m.media_url || null,
    fileName: m.file_name || null,
    // Email metadata (present only for email-sourced ticket messages)
    htmlContent: m.html_content || null,
    emailSubject: m.email_subject || null,
    emailCc: m.email_cc || null,
    emailBcc: m.email_bcc || null,
    emailToList: m.email_to_list || null,
    senderEmail: m.sender_email || null,
    sentBy: m.sent_by || null,
    sentByName: m.sent_by_name || null,
    structuredData: m.structured_data || null,
  };
};
